import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowRight, Check, Loader2, SearchCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, get, errMsg } from "@/lib/api";
import { FieldLabel, PageHeader, Panel, TagInput } from "@/admin/ui";
import { useMeta, useRefresh } from "@/admin/hooks";

const LEGAL = [["legal_name", "Titular o razón social", "Ej.: El Noticiero Media, S.L."], ["legal_nif", "NIF / CIF", "B12345678"],
  ["legal_address", "Domicilio", "Calle, número, código postal y ciudad"], ["legal_email", "Correo de contacto", "contacto@tudominio.es"],
  ["legal_phone", "Teléfono (opcional)", "+34 600 000 000"], ["legal_registry", "Datos registrales (opcional)", "Registro Mercantil de Madrid, tomo…, folio…, hoja…"]];

const LegalPanel = ({ f, set }) => (
  <Panel title="Datos del titular" description="Aparecen automáticamente en el aviso legal, la privacidad, las cookies y los términos de uso." testid="settings-legal-panel">
    <div id="titular" className="grid sm:grid-cols-2 gap-4 scroll-mt-24">
      {LEGAL.map(([k, label, ph]) => (
        <div key={k} className={k === "legal_address" || k === "legal_registry" ? "sm:col-span-2" : ""}>
          <FieldLabel>{label}</FieldLabel>
          <Input value={f[k] || ""} onChange={(e) => set({ [k]: e.target.value })} placeholder={ph} data-testid={`settings-${k.replace("_", "-")}-input`} />
        </div>
      ))}
    </div>
  </Panel>
);

export default function Settings() {
  const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: () => get("/admin/settings") });
  const { data: meta } = useMeta();
  const refresh = useRefresh();
  const [f, setF] = useState(null);
  useEffect(() => { if (data) setF(data); }, [data]);
  const set = (p) => setF((x) => ({ ...x, ...p }));
  const { hash } = useLocation();
  useEffect(() => { if (f && hash === "#titular") document.getElementById("titular")?.scrollIntoView({ behavior: "smooth", block: "center" }); }, [!!f, hash]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = useMutation({
    mutationFn: () => api.put("/admin/settings", f).then((r) => r.data),
    onSuccess: (d) => { setF(d); toast.success("Ajustes guardados"); refresh(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (!f) return <div className="py-20 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div data-testid="admin-settings-page">
      <PageHeader kicker="Configuración" title="Ajustes" description="La identidad de tu periódico, el estilo de la IA y quién puede entrar.">
        <Button onClick={() => save.mutate()} disabled={save.isPending} data-testid="settings-save-btn">{save.isPending && <Loader2 className="animate-spin" />}Guardar ajustes</Button>
      </PageHeader>
      <div className="grid xl:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Panel title="Tu periódico" testid="settings-identity-panel">
            <div className="space-y-4">
              <div><FieldLabel>Nombre</FieldLabel><Input value={f.site_name} onChange={(e) => set({ site_name: e.target.value })} data-testid="settings-site-name-input" /></div>
              <div><FieldLabel>Lema</FieldLabel><Input value={f.tagline} onChange={(e) => set({ tagline: e.target.value })} data-testid="settings-tagline-input" /></div>
              <div><FieldLabel hint={`${f.description.length}/160`}>Descripción (Google y redes)</FieldLabel><Textarea rows={3} value={f.description} onChange={(e) => set({ description: e.target.value })} data-testid="settings-description-input" /></div>
              <div><FieldLabel>Firma de las noticias</FieldLabel><Input value={f.author_name} onChange={(e) => set({ author_name: e.target.value })} data-testid="settings-author-input" /></div>
              <label className="flex items-center justify-between text-sm border border-border p-3">Mostrar la barra «Última hora»<Switch checked={f.ticker_enabled} onCheckedChange={(v) => set({ ticker_enabled: v })} data-testid="settings-ticker-switch" /></label>
            </div>
          </Panel>
          <LegalPanel f={f} set={set} />
          <Panel title="Equipo con acceso" description="Correos de Google que pueden entrar a la redacción." testid="settings-team-panel">
            <TagInput value={f.admin_emails} onChange={(admin_emails) => set({ admin_emails })} placeholder="correo@gmail.com" testid="settings-admin-emails" />
          </Panel>
        </div>
        <div className="space-y-6">
          <Panel title="Toque personal de la IA" description="Así escribe la IA cuando reescribe noticias o crea contenido propio." testid="settings-tone-panel">
            <div className="grid sm:grid-cols-2 gap-2">
              {meta?.tones.map((t) => (
                <button key={t.key} type="button" onClick={() => set({ default_tone: t.key })} data-testid={`settings-tone-${t.key}`}
                  className={`text-left border-2 p-3 transition-colors ${f.default_tone === t.key ? "border-primary bg-primary/5" : "border-border hover:border-foreground/40"}`}>
                  <p className="font-semibold text-sm flex items-center justify-between">{t.label}{f.default_tone === t.key && <Check className="h-4 w-4 text-primary" />}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>
                </button>
              ))}
            </div>
            <div className="mt-4">
              <FieldLabel hint="opcional">Instrucciones de la casa</FieldLabel>
              <Textarea rows={4} value={f.style_notes} onChange={(e) => set({ style_notes: e.target.value })} data-testid="settings-style-notes-input"
                placeholder="Ej.: Termina siempre con una frase sobre qué vigilar la próxima semana. Evita anglicismos. Menciona el impacto en pymes." />
            </div>
          </Panel>
          <Panel title="SEO y buscadores con IA (GEO)" description="Títulos, redes, organización, robots.txt, bots de IA, mapas del sitio, llms.txt, verificación y reglas de la IA." testid="settings-seo-panel">
            <Link to="/admin/seo" className="flex items-center justify-between gap-3 border-2 border-foreground px-4 py-3 font-semibold hover:bg-foreground hover:text-background transition-colors" data-testid="settings-open-seo-link">
              <span className="flex items-center gap-2"><SearchCheck className="h-4 w-4" /> Abrir ajustes de SEO y GEO</span><ArrowRight className="h-4 w-4" />
            </Link>
          </Panel>
        </div>
      </div>
    </div>
  );
}
